import type { NumberInputProps } from 'src/UIKit/components/NumberInput';
import { FilledInputProps, InputProps, OutlinedInputProps, SxProps, Theme } from '@mui/material';
import React, { ReactElement, ReactNode } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';
import { NumberInput } from 'src/UIKit/components/NumberInput';

export interface FormNumberInputProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  fieldName: Path<T>;

  label?: ReactNode;
  placeholder?: string;
  helperText?: ReactNode;
  validation?: RegisterOptions;

  disabled?: boolean;
  size?: NumberInputProps['size'];
  variant?: 'outlined' | 'filled' | 'standard';
  margin?: 'dense' | 'normal' | 'none';
  fullWidth?: boolean;
  sx?: SxProps<Theme>;

  format?: {
    style?: 'currency' | 'percent' | 'phoneNumber';
    currency?: 'USD' | 'EUR';
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
  };
  min?: number | undefined | null;
  max?: number | undefined | null;
  withoutOnBlurVoid?: boolean;
  inputProps?:
    | Partial<OutlinedInputProps>
    | Partial<FilledInputProps>
    | Partial<InputProps>
    | undefined;
}

export function FormNumberInput<T extends FieldValues>(
  props: FormNumberInputProps<T>,
): ReactElement | null {
  const {
    form,
    fieldName,
    label,
    placeholder,
    helperText,
    validation,
    disabled,
    size,
    variant,
    margin,
    fullWidth,
    sx,
    format,
    min,
    max,
    withoutOnBlurVoid,
    inputProps,
  } = props;

  return (
    <Controller
      name={fieldName}
      control={form.control as Control<FieldValues>}
      rules={validation}
      render={({ field, fieldState }) => {
        const { value, onChange } = field;
        const { error } = fieldState;
        return (
          <NumberInput
            inputProps={inputProps}
            label={label}
            placeholder={placeholder}
            value={+value}
            onChange={values =>
              onChange(typeof values.floatValue === 'number' ? values.floatValue : null)
            }
            error={!!error}
            helperText={error?.message || helperText}
            required={!!validation?.required}
            disabled={disabled}
            size={size}
            variant={variant}
            fullWidth={fullWidth}
            margin={margin}
            sx={sx}
            format={format}
            min={min}
            max={max}
            withoutOnBlurVoid={withoutOnBlurVoid}
          />
        );
      }}
    />
  );
}
